/**
 * Soumission d'exercice élève → prof (migration 0222) : l'élève envoie 1 à 3
 * photos + une note optionnelle à son prof particulier actif ; le prof les
 * retrouve dans sa boîte de réception (`/tutor/submissions`) et peut s'en
 * servir pour construire un générateur paramétrique (Blueprint).
 *
 * Écriture directe PostgREST (pas d'edge function pour l'upload/insert lui-
 * même — même pattern que `live_registrations`) ; la notification (push +
 * email) passe par l'edge fn existante `send-notification`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { sendNotification } from '@/lib/notifications/send-notification';

// exercise_submissions* sont postérieures à types.generated.ts (stale
// volontaire, cf. CLAUDE.md) → client générique + cast `as unknown as`.
const db = supabase as unknown as SupabaseClient;

const BUCKET = 'exercise-submissions';
export const MAX_SUBMISSION_PHOTOS = 3;

export type ExerciseSubmissionStatus = 'pending' | 'used' | 'dismissed';

export interface ExerciseSubmissionPhoto {
  id: string;
  storage_path: string;
  position: number;
}

export interface TutorSubmissionRow {
  id: string;
  student_id: string;
  tutor_id: string;
  note: string;
  status: ExerciseSubmissionStatus;
  blueprint_id: string | null;
  chapter_id: string | null;
  chapter_title: string | null;
  created_at: string;
  student_name: string;
  photos: ExerciseSubmissionPhoto[];
}

// ── Élève : envoyer une soumission ──────────────────────────────────────────

export interface SubmitExercisePhotosInput {
  studentId: string;
  tutorId: string;
  note: string;
  chapterId: string | null;
  photos: Blob[];
}

export function useSubmitExercisePhotos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      studentId,
      tutorId,
      note,
      chapterId,
      photos,
    }: SubmitExercisePhotosInput): Promise<string> => {
      const { data: submission, error: subErr } = await db
        .from('exercise_submissions')
        .insert({
          student_id: studentId,
          tutor_id: tutorId,
          note: note.trim(),
          chapter_id: chapterId,
        })
        .select('id')
        .single();
      if (subErr || !submission) throw new Error(subErr?.message ?? 'Échec de la soumission');
      const submissionId = (submission as { id: string }).id;

      for (let i = 0; i < photos.length; i++) {
        const path = `${studentId}/${submissionId}/${i}.jpg`;
        const { error: upErr } = await db.storage.from(BUCKET).upload(path, photos[i]!, {
          contentType: 'image/jpeg',
          upsert: false,
        });
        if (upErr) throw new Error(upErr.message);

        const { error: photoErr } = await db
          .from('exercise_submission_photos')
          .insert({ submission_id: submissionId, storage_path: path, position: i });
        if (photoErr) throw new Error(photoErr.message);
      }

      return submissionId;
    },
    onSuccess: (submissionId) => {
      qc.invalidateQueries({ queryKey: ['exercise-submissions'] });
      // Best-effort — ne bloque jamais l'UI élève (cf. sendNotification).
      void sendNotification({ type: 'exercise_photo_submitted', submission_id: submissionId });
    },
  });
}

// ── Prof : boîte de réception ───────────────────────────────────────────────

const SUBMISSION_SELECT = `id, student_id, tutor_id, note, status, blueprint_id, chapter_id, created_at,
  profiles!exercise_submissions_student_id_fkey(display_name:visible_name),
  chapters(title),
  exercise_submission_photos(id, storage_path, position)`;

interface RawSubmissionRow {
  id: string;
  student_id: string;
  tutor_id: string;
  note: string;
  status: ExerciseSubmissionStatus;
  blueprint_id: string | null;
  chapter_id: string | null;
  created_at: string;
  profiles: { display_name: string } | { display_name: string }[] | null;
  chapters: { title: string } | { title: string }[] | null;
  exercise_submission_photos: ExerciseSubmissionPhoto[] | null;
}

function mapSubmissionRow(row: unknown): TutorSubmissionRow {
  const r = row as RawSubmissionRow;
  const profile = Array.isArray(r.profiles) ? r.profiles[0] : r.profiles;
  const chapter = Array.isArray(r.chapters) ? r.chapters[0] : r.chapters;
  return {
    id: r.id,
    student_id: r.student_id,
    tutor_id: r.tutor_id,
    note: r.note,
    status: r.status,
    blueprint_id: r.blueprint_id,
    chapter_id: r.chapter_id,
    chapter_title: chapter?.title ?? null,
    created_at: r.created_at,
    student_name: profile?.display_name ?? 'Un élève',
    photos: [...(r.exercise_submission_photos ?? [])].sort((a, b) => a.position - b.position),
  };
}

export function useTutorSubmissions(tutorId: string | null | undefined) {
  return useQuery({
    queryKey: ['exercise-submissions', tutorId],
    enabled: !!tutorId,
    queryFn: async (): Promise<TutorSubmissionRow[]> => {
      const { data, error } = await db
        .from('exercise_submissions')
        .select(SUBMISSION_SELECT)
        .eq('tutor_id', tutorId as string)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []).map(mapSubmissionRow);
    },
    staleTime: 30_000,
  });
}

/** Une soumission précise (ex. préremplissage du formulaire Blueprint depuis `?submission=`). */
export function useExerciseSubmission(submissionId: string | null | undefined) {
  return useQuery({
    queryKey: ['exercise-submission', submissionId],
    enabled: !!submissionId,
    queryFn: async (): Promise<TutorSubmissionRow> => {
      const { data, error } = await db
        .from('exercise_submissions')
        .select(SUBMISSION_SELECT)
        .eq('id', submissionId as string)
        .single();
      if (error || !data) throw new Error(error?.message ?? 'Soumission introuvable');
      return mapSubmissionRow(data);
    },
  });
}

/** URL signée temporaire (bucket privé) pour afficher une photo de soumission. */
export function useSubmissionPhotoUrl(storagePath: string | null | undefined) {
  return useQuery({
    queryKey: ['exercise-submission-photo-url', storagePath],
    enabled: !!storagePath,
    queryFn: async (): Promise<string> => {
      const { data, error } = await db.storage
        .from(BUCKET)
        .createSignedUrl(storagePath as string, 60 * 30);
      if (error || !data) throw new Error(error?.message ?? 'URL signée indisponible');
      return data.signedUrl;
    },
    staleTime: 15 * 60_000,
  });
}

export function useMarkSubmissionStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
      blueprintId,
    }: {
      id: string;
      status: ExerciseSubmissionStatus;
      blueprintId?: string;
    }) => {
      const { error } = await db
        .from('exercise_submissions')
        .update({ status, ...(blueprintId ? { blueprint_id: blueprintId } : {}) })
        .eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exercise-submissions'] });
    },
  });
}
